import { useState, useRef, useEffect } from 'react'
import { useRouter } from '@tanstack/react-router'
import { cn } from '@workspace/ui/lib/utils'
import { Button } from '@workspace/ui/components/Button'
import { NotificationList } from './NotificationList'
import { NotificationTypeEnum, type Notification, type NotificationCategory } from '@workspace/lib/api/notification'
import {
    useMarkAllNotificationsRead,
    useNotifications,
    useToggleReadNotification,
} from '@/shared/hooks/use-notifications'

interface NotificationCenterProps {
    className?: string
}

const taskTypes: string[] = [
    NotificationTypeEnum.TASK_ASSIGNED,
    NotificationTypeEnum.TASK_NEARDUE,
    NotificationTypeEnum.TASK_OVERDUE,
    NotificationTypeEnum.TASK_DEADLINE_CHANGED,
    NotificationTypeEnum.TASK_COMMENT,
    NotificationTypeEnum.NEW_COMMENT,
    NotificationTypeEnum.REQUEST_APPROVAL_REMINDER,
    // NOTE: SUBORDINATE_PICKED_TASK_FROM_OTHER_UNIT intentionally excluded
    // Manager cannot view the task (403) after subordinate accepts it
]

const approvalType: string[] = [
    NotificationTypeEnum.NEW_REQUEST_APPROVAL,
    NotificationTypeEnum.REQUEST_APPROVAL_REJECTED,
    NotificationTypeEnum.REQUEST_APPROVAL_APPROVED,
]

export function NotificationCenter({ className }: NotificationCenterProps) {
    const router = useRouter()
    const [activeCategory, setActiveCategory] = useState<NotificationCategory | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    const {
        approvals,
        generals,
        warnings,
        unReadCounts,
        fetchNextPageStandard,
        hasNextPageStandard,
        isFetchingNextPageStandard,
        fetchNextPageApproval,
        hasNextPageApproval,
        isFetchingNextPageApproval,
        fetchNextPageWarning,
        hasNextPageWarning,
        isFetchingNextPageWarning,
    } = useNotifications()
    const { mutate: toggleRead } = useToggleReadNotification()
    const { mutate: markAllRead } = useMarkAllNotificationsRead()

    const getNotifications = (category: NotificationCategory): Notification[] => {
        if (category === 'approval') return approvals
        if (category === 'warning') return warnings
        return generals
    }

    const getUnreadCount = (category: NotificationCategory): number => {
        return unReadCounts[category] ?? 0
    }

    // Click outside để đóng dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setActiveCategory(null)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleToggle = (category: NotificationCategory) => {
        setActiveCategory(prev => (prev === category ? null : category))
    }

    const handleItemClick = (notification: Notification) => {
        if (!notification.isRead) {
            toggleRead({ id: notification.id, isRead: true })
        }

        if (taskTypes.includes(notification.type)) {
            return router.navigate({
                to: `/legal-documents/tasks`,
                search: {
                    searchId: notification.task?.id,
                    type: 'TASK',
                },
            })
        }

        if (approvalType.includes(notification.type)) {
            return router.navigate({
                to: `/legal-documents/approvals`,
                search: {
                    searchId: notification.metadata?.requestApprovalId,
                    type: 'APPROVAL',
                },
            })
        }

        if (
            notification.type === NotificationTypeEnum.WEEKLY_REPORT_CREATED ||
            notification.type === NotificationTypeEnum.WEEKLY_REPORT_UPDATED
        ) {
            return router.navigate({
                to: `/legal-documents/tasks`,
                search: {
                    openWeeklyReport: true,
                    week: notification.metadata?.week,
                    year: notification.metadata?.year,
                },
            })
        }
        if (notification.type === NotificationTypeEnum.ROLE_CHANGED) {
            return router.navigate({
                to: `/profile`,
            })
        }

        if (notification.type === NotificationTypeEnum.PROJECT_ITEM_CHANGED) {
            const projectId = notification.metadata?.projectId || notification.task?.projectId
            if (!projectId) return

            const itemType = notification.metadata?.itemType?.toLowerCase()
            const isTask = notification.task || itemType === 'task' || itemType === 'công việc'

            if (isTask) {
                // Chỉ lấy ID liên quan đến Task, tránh lấy documentId gây lỗi API 404
                const taskId = notification.task?.id || notification.metadata?.taskId || notification.metadata?.itemId

                return router.navigate({
                    to: `/projects/${projectId}/tasks`,
                    search: {
                        taskId,
                    },
                })
            } else {
                // Lấy ID liên quan đến Document (ưu tiên documentId, fallback sang itemId)
                const searchId = notification.metadata?.documentId || notification.metadata?.itemId

                return router.navigate({
                    to: `/projects/${projectId}/project-profile`,
                    search: {
                        searchId,
                    },
                })
            }
        }
    }

    const handleMarkAllAsRead = () => {
        markAllRead()
    }

    const handleViewAll = (category: NotificationCategory) => {
        router.navigate({
            to: '/notifications',
            search: {
                tab: category,
                page: 1,
            },
        })
        setActiveCategory(null)
    }

    const handleViewAllFooter = (category: NotificationCategory) => {
        router.navigate({
            to: '/notifications',
        })
        setActiveCategory(null)
    }

    const iconButtons: { category: NotificationCategory; icon: string; label: string }[] = [
        { category: 'approval', icon: '/check-circle.svg', label: 'Phê duyệt' },
        { category: 'warning', icon: '/warn.svg', label: 'Cảnh báo' },
        { category: 'general', icon: '/bell.svg', label: 'Thông báo' },
    ]

    return (
        <div ref={containerRef} className={cn('relative flex items-center gap-4', className)}>
            {iconButtons.map(({ category, icon: Icon, label }) => {
                const unreadCount = getUnreadCount(category)
                const isActive = activeCategory === category

                return (
                    <div key={category} className="relative">
                        <Button
                            variant="ghost"
                            size="icon"
                            className={cn('relative', isActive && 'bg-neutral-100 dark:bg-neutral-800')}
                            onClick={() => handleToggle(category)}
                            aria-label={label}
                        >
                            <span
                                className="size-5! bg-foreground"
                                style={{
                                    maskImage: `url(${Icon})`,
                                    WebkitMaskImage: `url(${Icon})`,
                                    maskSize: 'contain',
                                    WebkitMaskSize: 'contain',
                                    maskRepeat: 'no-repeat',
                                    WebkitMaskRepeat: 'no-repeat',
                                    maskPosition: 'center',
                                    WebkitMaskPosition: 'center',
                                }}
                            />
                            {unreadCount > 0 && (
                                <span className="absolute -top-1 left-1/2 min-w-4.5 h-4.5 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-medium">
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                </span>
                            )}
                        </Button>

                        {/* Dropdown */}
                        {isActive && (
                            <div className="absolute top-full right-0 mt-2 z-50">
                                <NotificationList
                                    category={category}
                                    notifications={getNotifications(category)}
                                    hasNextPage={
                                        category === 'approval'
                                            ? hasNextPageApproval
                                            : category === 'warning'
                                              ? hasNextPageWarning
                                              : hasNextPageStandard
                                    }
                                    fetchNextPage={
                                        category === 'approval'
                                            ? fetchNextPageApproval
                                            : category === 'warning'
                                              ? fetchNextPageWarning
                                              : fetchNextPageStandard
                                    }
                                    isFetchingNextPage={
                                        category === 'approval'
                                            ? isFetchingNextPageApproval
                                            : category === 'warning'
                                              ? isFetchingNextPageWarning
                                              : isFetchingNextPageStandard
                                    }
                                    onItemClick={handleItemClick}
                                    onMarkAllAsRead={handleMarkAllAsRead}
                                    onViewAll={() => handleViewAll(category)}
                                    onViewAllFooter={() => handleViewAllFooter(category)}
                                    onClose={() => setActiveCategory(null)}
                                />
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}
